import type { SpeechSession } from '@hugo/core'
import {
  SPEECH_PROTOCOL,
  SPEECH_TOKEN_PROTOCOL_PREFIX,
  type SpeechClientMessage,
  speechServerMessageSchema,
} from './protocol'

/**
 * The client side of a speech session (browser-safe: WebSocket only, no Node APIs). It speaks the
 * protocol of the session's provider; for now hugo speech protocol v1 (the fake service).
 */

export type SpeechStreamError =
  /** The service refused the session (bad or expired token). */
  | 'unauthorized'
  /** The connection failed or dropped before a final transcript. */
  | 'connection'
  /** The service reported a problem. */
  | 'service'

export type SpeechStreamHandlers = {
  onPartial?: (text: string) => void
  onFinal: (text: string) => void
  onError: (error: SpeechStreamError, message: string) => void
}

export type SpeechStream = {
  /** 16 kHz mono PCM16 audio; queued until the service is ready. */
  sendAudio: (pcm: ArrayBuffer) => void
  /** The user is done: the service sends the final transcript, then closes. */
  stop: () => void
  /** Give up without a transcript. */
  cancel: () => void
}

/** Audio held back until the service is ready: at most this many frames (100 ms each). */
const QUEUE_MAX = 50

export function openSpeechStream(
  session: SpeechSession,
  handlers: SpeechStreamHandlers,
  WebSocketImpl: typeof WebSocket = globalThis.WebSocket,
): SpeechStream {
  const socket = new WebSocketImpl(session.url, [
    SPEECH_PROTOCOL,
    `${SPEECH_TOKEN_PROTOCOL_PREFIX}${session.token}`,
  ])
  socket.binaryType = 'arraybuffer'
  let ready = false
  let done = false
  let stopRequested = false
  const queue: ArrayBuffer[] = []
  const send = (message: SpeechClientMessage) => socket.send(JSON.stringify(message))
  const fail = (error: SpeechStreamError, message: string) => {
    if (done) return
    done = true
    handlers.onError(error, message)
    if (socket.readyState === socket.OPEN) socket.close(1000)
  }

  socket.addEventListener('open', () => {
    // Cancelled while connecting: closing then would log an error in browsers; close now.
    if (done) {
      socket.close(1000)
      return
    }
    send({
      type: 'start',
      sampleRate: session.sampleRate,
      encoding: session.encoding,
      language: session.language,
    })
  })
  socket.addEventListener('message', (event) => {
    if (done || typeof event.data !== 'string') return
    let parsed: ReturnType<typeof speechServerMessageSchema.safeParse>
    try {
      parsed = speechServerMessageSchema.safeParse(JSON.parse(event.data))
    } catch {
      return
    }
    if (!parsed.success) return
    const message = parsed.data
    if (message.type === 'ready') {
      ready = true
      for (const pcm of queue.splice(0)) socket.send(pcm)
      if (stopRequested) send({ type: 'stop' })
    } else if (message.type === 'partial') {
      handlers.onPartial?.(message.text)
    } else if (message.type === 'final') {
      done = true
      handlers.onFinal(message.text)
    } else {
      fail(message.code === 'unauthorized' ? 'unauthorized' : 'service', message.message)
    }
  })
  // A refused session (401 before the upgrade) also shows up here, as a close without an open.
  socket.addEventListener('close', () => fail('connection', 'The connection closed'))
  socket.addEventListener('error', () => fail('connection', 'The connection failed'))

  return {
    sendAudio: (pcm) => {
      if (done || stopRequested) return
      if (ready && socket.readyState === socket.OPEN) socket.send(pcm)
      else if (queue.length < QUEUE_MAX) queue.push(pcm)
    },
    stop: () => {
      if (done || stopRequested) return
      stopRequested = true
      if (ready && socket.readyState === socket.OPEN) send({ type: 'stop' })
    },
    cancel: () => {
      if (done) return
      done = true
      // Still connecting: it closes as soon as it opens (see above).
      if (socket.readyState === socket.OPEN) socket.close(1000)
    },
  }
}
